import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '../../../lib/api'
import type { ProgramRecord } from '../../../types/programs'
import type { ProspectCreatePayload } from '../../../types/prospects'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AlertCircleIcon, ChevronLeft } from 'lucide-react'

interface NewProspectDialogProps {
  open: boolean
  programs: ProgramRecord[]
  defaultProgramId?: string | null
  isSubmitting: boolean
  error: ApiError | null
  onClose: () => void
  onSubmit: (payload: ProspectCreatePayload) => Promise<void>
  onBack?: () => void
}

type FormState = {
  program_id: string
  contact_name: string
  contact_email: string
  contact_phone_raw: string
  company_name: string
}

function buildInitialState(programs: ProgramRecord[], defaultProgramId?: string | null): FormState {
  const preferredProgramId =
    defaultProgramId && programs.some((program) => program.id === defaultProgramId)
      ? defaultProgramId
      : programs[0]?.id ?? ''

  return {
    program_id: preferredProgramId,
    contact_name: '',
    contact_email: '',
    contact_phone_raw: '',
    company_name: '',
  }
}

function fieldError(error: ApiError | null, field: string) {
  return error?.errors?.[field]?.[0] ?? null
}

export function NewProspectDialog({
  open,
  programs,
  defaultProgramId,
  isSubmitting,
  error,
  onClose,
  onSubmit,
  onBack,
}: NewProspectDialogProps) {
  const { t } = useTranslation()
  const [form, setForm] = useState<FormState>(() => buildInitialState(programs, defaultProgramId))
  const [clientError, setClientError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setForm(buildInitialState(programs, defaultProgramId))
    setClientError(null)
  }, [defaultProgramId, open, programs])

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose() }}>
      <DialogContent className="sm:max-w-md w-full p-0 shadow-none rounded-[0.625rem] overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-border">
          <div className="flex items-center gap-2.5">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="flex items-center justify-center size-7 rounded-[0.625rem] hover:bg-muted text-muted-foreground hover:text-foreground transition-colors shrink-0"
                aria-label={t('prospects.dialogs.newProspect.backAriaLabel')}
              >
                <ChevronLeft className="size-4" />
              </button>
            )}
            <div>
              <DialogTitle className="text-sm font-semibold leading-none">{t('prospects.dialogs.newProspect.title')}</DialogTitle>
              <DialogDescription className="text-xs mt-1">
                {t('prospects.dialogs.newProspect.description')}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form
          id="new-prospect-form"
          className="px-6 py-5 max-h-[62vh] space-y-4 overflow-y-auto"
          onSubmit={async (event) => {
            event.preventDefault()

            if (form.contact_email.trim().length === 0 && form.contact_phone_raw.trim().length === 0) {
              setClientError(t('prospects.dialogs.newProspect.contactRequiredError'))
              return
            }

            setClientError(null)

            await onSubmit({
              program_id: form.program_id,
              contact_name: form.contact_name.trim(),
              contact_email: form.contact_email.trim() || null,
              contact_phone_raw: form.contact_phone_raw.trim() || null,
              company_name: form.company_name.trim() || null,
            })
          }}
        >
          <div className="space-y-2">
            <label className="text-sm text-muted-foreground" htmlFor="prospect-program">
              {t('prospects.dialogs.newProspect.assignedProgram')}
            </label>
            <Select
              value={form.program_id}
              onValueChange={(value) => setForm((current) => ({ ...current, program_id: value }))}
            >
              <SelectTrigger id="prospect-program" className="w-full">
                <SelectValue placeholder={t('prospects.dialogs.newProspect.selectProgramPlaceholder')} />
              </SelectTrigger>
              <SelectContent position="popper" className="w-[var(--radix-popper-anchor-width)]">
                {programs.map((program) => (
                  <SelectItem key={program.id} value={program.id} className="max-w-full">
                    <span className="block truncate">{program.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldError(error, 'program_id') ? (
              <p className="text-xs text-destructive">{fieldError(error, 'program_id')}</p>
            ) : null}
          </div>

          <div className="space-y-3">
            <div className="space-y-2">
              <label className="text-sm text-muted-foreground" htmlFor="prospect-contact-name">{t('prospects.dialogs.newProspect.contactNameLabel')}</label>
              <Input
                id="prospect-contact-name"
                value={form.contact_name}
                onChange={(event) => setForm((current) => ({ ...current, contact_name: event.target.value }))}
                placeholder={t('prospects.dialogs.newProspect.contactNamePlaceholder')}
                required
              />
              {fieldError(error, 'contact_name') ? (
                <p className="text-xs text-destructive">{fieldError(error, 'contact_name')}</p>
              ) : null}
            </div>

            <div className="space-y-2">
              <label className="text-sm text-muted-foreground" htmlFor="prospect-company">{t('prospects.dialogs.newProspect.companyLabel')}</label>
              <Input
                id="prospect-company"
                value={form.company_name}
                onChange={(event) => setForm((current) => ({ ...current, company_name: event.target.value }))}
                placeholder={t('prospects.dialogs.newProspect.companyPlaceholder')}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-muted-foreground" htmlFor="prospect-email">{t('prospects.dialogs.newProspect.emailLabel')}</label>
              <Input
                id="prospect-email"
                type="email"
                value={form.contact_email}
                onChange={(event) => setForm((current) => ({ ...current, contact_email: event.target.value }))}
                placeholder={t('prospects.dialogs.newProspect.emailPlaceholder')}
              />
              {fieldError(error, 'contact_email') ? (
                <p className="text-xs text-destructive">{fieldError(error, 'contact_email')}</p>
              ) : null}
            </div>

            <div className="space-y-2">
              <label className="text-sm text-muted-foreground" htmlFor="prospect-phone">{t('prospects.dialogs.newProspect.phoneLabel')}</label>
              <Input
                id="prospect-phone"
                value={form.contact_phone_raw}
                onChange={(event) => setForm((current) => ({ ...current, contact_phone_raw: event.target.value }))}
                placeholder={t('prospects.dialogs.newProspect.phonePlaceholder')}
              />
              {fieldError(error, 'contact_phone_raw') ? (
                <p className="text-xs text-destructive">{fieldError(error, 'contact_phone_raw')}</p>
              ) : null}
            </div>
          </div>

          {clientError ? (
            <Alert variant="destructive">
              <AlertCircleIcon />
              <AlertTitle>{t('prospects.dialogs.newProspect.contactRequiredAlertTitle')}</AlertTitle>
              <AlertDescription>{clientError}</AlertDescription>
            </Alert>
          ) : null}

          {error && !error.errors ? (
            <Alert variant="destructive">
              <AlertCircleIcon />
              <AlertTitle>{t('prospects.dialogs.newProspect.createErrorAlertTitle')}</AlertTitle>
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
          ) : null}
        </form>

        <DialogFooter className="px-6 pb-5 pt-0">
          <DialogClose asChild>
            <Button type="button" variant="outline" size="sm" disabled={isSubmitting}>
              {t('prospects.dialogs.newProspect.cancel')}
            </Button>
          </DialogClose>
          <Button type="submit" form="new-prospect-form" size="sm" disabled={isSubmitting || !programs.length}>
            {isSubmitting ? t('prospects.dialogs.newProspect.submitting') : t('prospects.dialogs.newProspect.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
